import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Wallet, PieChart, Receipt, Users, TrendingUp, Shield, 
  Smartphone, Zap, ChevronRight, Star, Check, Mail, 
  MessageSquare, ArrowRight, Play, Sparkles, Target,
  CreditCard, Bell, Globe, ChevronDown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const LandingPage = ({ onGetStarted }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-rotate features demo
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFormSubmitted(true);
        toast.success('Message sent successfully!');
        setContactForm({ name: '', email: '', message: '' });
        setTimeout(() => setFormSubmitted(false), 3000);
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Wallet className="w-6 h-6" />,
      title: "Smart Budget Tracking",
      description: "Effortlessly track income and expenses with intelligent categorization",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: <Receipt className="w-6 h-6" />,
      title: "AI Receipt Scanner",
      description: "Snap a photo and let AI extract expenses automatically",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Split Expenses",
      description: "Share costs with friends and track who owes what",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Smart Analytics",
      description: "AI-powered insights to optimize your spending habits",
      color: "from-orange-500 to-amber-500"
    }
  ];

  const demoScreens = [
    {
      title: "Dashboard Overview",
      description: "See your complete financial picture at a glance",
      image: "https://static.prod-images.emergentagent.com/jobs/a83f1eb1-9109-4443-91c8-edd5afda21a9/images/0bfa24c5c3b5d302b8e672acc7d733445a99e64b02be2a7a4e280ab3b033e782.png",
      highlights: ["$3,216 Balance", "Spending breakdown", "Budget progress"]
    },
    {
      title: "AI Receipt Scanner",
      description: "Just snap a photo - AI does the rest",
      image: "https://static.prod-images.emergentagent.com/jobs/a83f1eb1-9109-4443-91c8-edd5afda21a9/images/ecb867e50b03db22b6c9ae477f03cc84f28ec9fc75f6d78b19c7fca5b4b381b2.png",
      highlights: ["Auto-categorization", "Multi-item extraction", "Smart suggestions"]
    },
    {
      title: "Split with Friends",
      description: "Never argue about money again",
      image: "https://static.prod-images.emergentagent.com/jobs/a83f1eb1-9109-4443-91c8-edd5afda21a9/images/84a48772cfc9441e07fd2b418ad8369e9db7f587d84d7df3158ca4513409924a.png",
      highlights: ["Group expenses", "Balance tracking", "Easy settlements"]
    },
    {
      title: "Smart Insights",
      description: "AI finds savings opportunities for you",
      image: "https://static.prod-images.emergentagent.com/jobs/a83f1eb1-9109-4443-91c8-edd5afda21a9/images/ff91636d78487c05626c2ba62b4ac1dd43f60ce65e01d7abde0d87ef7232384b.png",
      highlights: ["A Grade Health Score", "58% Savings Rate", "Personalized tips"]
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      role: "Freelancer",
      text: "SnapTally helped me save $400/month just by showing me where my money was going!",
      rating: 5
    },
    {
      name: "James K.",
      role: "Student",
      text: "The split feature is a lifesaver for tracking shared apartment expenses.",
      rating: 5
    },
    {
      name: "Lisa T.",
      role: "Small Business Owner",
      text: "The AI receipt scanner saves me hours every week on expense tracking.",
      rating: 5
    }
  ];

  const pricingTiers = [
    {
      name: "Essentials",
      price: "Free",
      description: "Perfect for getting started",
      features: ["10 AI scans/month", "15 transactions/month", "Basic analytics", "Single currency"],
      highlighted: false
    },
    {
      name: "Prime",
      price: "$4.99",
      period: "/month",
      description: "For serious budgeters",
      features: ["100 AI scans/month", "50 transactions/month", "Smart insights", "Trips & Projects", "Priority support"],
      highlighted: true
    },
    {
      name: "Elite",
      price: "$9.99",
      period: "/month",
      description: "Unlimited everything",
      features: ["Unlimited scans", "Unlimited transactions", "Predictive budgeting", "Hidden fee detection", "Export reports"],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_smarttally-3/artifacts/m32h86uo_download%20%283%29.png" 
              alt="SnapTally" 
              className="h-10 w-auto"
            />
            <span className="font-bold text-xl text-foreground">SnapTally</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#demo" className="text-muted-foreground hover:text-foreground transition-colors">Demo</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
            <a href="#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
          <Button onClick={onGetStarted} className="bg-primary hover:bg-primary/90">
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Budget Planning
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Take Control of Your
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Finances</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-lg">
                Smart budgeting made simple. Track expenses, split costs with friends, and get AI-powered insights to save more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button 
                  size="lg" 
                  onClick={onGetStarted}
                  className="bg-primary hover:bg-primary/90 text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8 py-6 rounded-xl"
                  onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 justify-center md:justify-start">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 border-2 border-background flex items-center justify-center">
                      <span className="text-xs font-medium">{['SM', 'JK', 'LT', 'AR'][i-1]}</span>
                    </div>
                  ))}
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">Loved by 10,000+ users</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-3xl" />
              <div className="relative bg-gradient-to-br from-card to-muted rounded-3xl p-6 shadow-elevated border border-border/50">
                <div className="bg-background rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-muted-foreground">February 2026</span>
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">+12% savings</span>
                  </div>
                  <div className="text-3xl font-bold text-foreground mb-1">$4,280.50</div>
                  <p className="text-sm text-muted-foreground mb-6">Total Balance</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 rounded-xl p-3">
                      <p className="text-xs text-green-600 mb-1">Income</p>
                      <p className="text-lg font-semibold text-green-700">$6,500</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3">
                      <p className="text-xs text-red-600 mb-1">Expenses</p>
                      <p className="text-lg font-semibold text-red-700">$2,219</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {['Food & Dining', 'Transportation', 'Entertainment'].map((cat, i) => (
                      <div key={cat} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${['bg-orange-400', 'bg-blue-400', 'bg-pink-400'][i]}`} />
                        <span className="text-sm text-muted-foreground flex-1">{cat}</span>
                        <span className="text-sm font-medium">${[420, 180, 95][i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Master Your Money</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed to make budgeting effortless and even enjoyable.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="group hover:shadow-elevated transition-all duration-300 cursor-pointer border-border/50 hover:border-primary/30"
                onClick={() => setActiveFeature(index)}
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: <Globe className="w-5 h-5" />, title: "Multi-Currency", desc: "Support for 8+ currencies with live exchange rates" },
              { icon: <Bell className="w-5 h-5" />, title: "Smart Alerts", desc: "Get notified when you're overspending" },
              { icon: <Shield className="w-5 h-5" />, title: "Bank-Level Security", desc: "Your data is encrypted and secure" },
              { icon: <Smartphone className="w-5 h-5" />, title: "Works Offline", desc: "Access your data even without internet" },
              { icon: <Target className="w-5 h-5" />, title: "Budget Goals", desc: "Set and track category-specific budgets" },
              { icon: <CreditCard className="w-5 h-5" />, title: "Subscription Tracking", desc: "Never forget a recurring payment" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              See SnapTally in Action
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience our star features that make budgeting a breeze.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Feature Tabs */}
            <div className="space-y-4">
              {demoScreens.map((screen, index) => (
                <div
                  key={index}
                  className={`p-5 rounded-2xl cursor-pointer transition-all duration-300 ${
                    activeFeature === index 
                      ? 'bg-primary text-primary-foreground shadow-lg' 
                      : 'bg-muted/50 hover:bg-muted'
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      activeFeature === index ? 'bg-white/20' : 'bg-primary/10'
                    }`}>
                      {[<Wallet />, <Receipt />, <Users />, <TrendingUp />][index]}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{screen.title}</h3>
                      <p className={`text-sm ${activeFeature === index ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                        {screen.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${activeFeature === index ? 'rotate-90' : ''}`} />
                  </div>
                  {activeFeature === index && (
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <div className="flex flex-wrap gap-2">
                        {screen.highlights.map((highlight, i) => (
                          <span key={i} className="px-3 py-1 bg-white/20 rounded-full text-xs">
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Demo Preview */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-2xl" />
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-3 shadow-2xl">
                {/* Phone Frame */}
                <div className="bg-black rounded-[2rem] p-2">
                  <div className="bg-background rounded-[1.5rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="bg-background px-6 py-2 flex items-center justify-between">
                      <span className="text-xs font-medium">9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 border border-foreground/50 rounded-sm">
                          <div className="w-3/4 h-full bg-green-500 rounded-sm" />
                        </div>
                      </div>
                    </div>
                    {/* Screen Content */}
                    <div className="aspect-[9/16] relative overflow-hidden">
                      <img 
                        src={demoScreens[activeFeature].image}
                        alt={demoScreens[activeFeature].title}
                        className="w-full h-full object-cover transition-all duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h4 className="text-xl font-bold mb-2">{demoScreens[activeFeature].title}</h4>
                        <p className="text-white/80 text-sm">{demoScreens[activeFeature].description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Loved by Budgeters Everywhere
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-foreground mb-4">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-medium">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start free and upgrade as you grow. No hidden fees, cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative ${tier.highlighted ? 'border-primary shadow-elevated scale-105' : 'border-border/50'}`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">{tier.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{tier.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-foreground">{tier.price}</span>
                    {tier.period && <span className="text-muted-foreground">{tier.period}</span>}
                  </div>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${tier.highlighted ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={tier.highlighted ? 'default' : 'outline'}
                    onClick={onGetStarted}
                  >
                    {tier.price === 'Free' ? 'Get Started' : 'Start Free Trial'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Get in Touch
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Email Us</p>
                    <p className="text-muted-foreground">support@snaptally.ai</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Live Chat</p>
                    <p className="text-muted-foreground">Available Mon-Fri, 9am-5pm EST</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-border/50">
              <CardContent className="p-6">
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground">We'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                      <Input
                        placeholder="Your name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Message</label>
                      <Textarea
                        placeholder="How can we help you?"
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="contact-submit-btn">
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-primary to-secondary rounded-3xl p-8 md:p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Take Control of Your Finances?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of users who are already saving more and stressing less with SnapTally.
            </p>
            <Button 
              size="lg" 
              variant="secondary"
              onClick={onGetStarted}
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 rounded-xl"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smarttally-3/artifacts/m32h86uo_download%20%283%29.png" 
                  alt="SnapTally" 
                  className="h-8 w-auto"
                />
                <span className="font-bold text-lg">SnapTally</span>
              </div>
              <p className="text-muted-foreground text-sm">
                Smart budgeting for everyone. Take control of your finances with AI-powered insights.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="#demo" className="hover:text-foreground transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-foreground transition-colors">About</a></li>
                <li><a href="#contact" className="hover:text-foreground transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 SnapTally. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Made with ❤️ for better finances</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
